import axios from '../../utils/axios';
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { dedupeEntities } from '../../utils/collectionUtils';
import { promoFieldMatches } from '../../utils/promoCodes';

// Paths are relative to the axios baseURL of /api/v1

async function verifySavedPromo(saved, expected, id, code) {
  try {
    const savedId = id || saved?._id;
    const savedCode = code || saved?.code;
    if (!savedCode) throw new Error('Missing saved code');
    const response = await axios.get('/promo-codes/admin', {
      params: { page: 1, limit: 20, code: savedCode, _verifiedAt: Date.now() },
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    const data = response.data.data || response.data;
    const stored = data.promoCodes?.find((promo) =>
      promo.code === savedCode && (!savedId || promo._id === savedId));
    if (!stored || !Object.entries(expected).every(([key, value]) => promoFieldMatches(key, stored[key], value))) {
      throw new Error('Stored terms do not match');
    }
    return stored;
  } catch {
    const error = new Error('Save was accepted, but the stored promo terms could not be verified. Refresh promo codes before making another change.');
    error.saved = true;
    throw error;
  }
}

export const fetchPromoCodes = createAsyncThunk(
  'promoCode/fetchAll',
  async ({ page = 1, limit = 20, active, code } = {}, { rejectWithValue }) => {
    try {
      const params = { page, limit };
      if (active !== undefined) params.active = active;
      if (code) params.code = code;
      const res = await axios.get('/promo-codes/admin', { params });
      const payload = res.data.data || res.data;
      return {
        promoCodes: payload.promoCodes || [],
        pagination: payload.pagination || { page: 1, limit, total: 0, pages: 1 },
      };
    } catch (err) {
      return rejectWithValue(err.response?.data?.message || 'Failed to fetch promo codes');
    }
  }
);

export const createPromoCode = createAsyncThunk(
  'promoCode/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await axios.post('/promo-codes/admin', payload);
      return await verifySavedPromo(res.data.promoCode || res.data.data?.promoCode || res.data, payload, null, payload.code);
    } catch (err) {
      return rejectWithValue(err.saved ? err.message : err.response?.data?.message || 'Failed to create promo code', { saved: Boolean(err.saved) });
    }
  }
);

export const updatePromoCode = createAsyncThunk(
  'promoCode/update',
  async ({ id, code, ...payload }, { rejectWithValue }) => {
    try {
      const res = await axios.patch(`/promo-codes/admin/${id}`, payload);
      return await verifySavedPromo(res.data.promoCode || res.data.data?.promoCode || res.data, payload, id, code);
    } catch (err) {
      return rejectWithValue(err.saved ? err.message : err.response?.data?.message || 'Failed to update promo code', { saved: Boolean(err.saved) });
    }
  }
);

const initialState = {
  codes: [],
  pagination: { page: 1, limit: 20, total: 0, pages: 1 },
  loading: false,
  actionLoading: false,
  error: null,
};

const promoCodeSlice = createSlice({
  name: 'promoCode',
  initialState,
  reducers: {
    clearPromoError: (state) => { state.error = null; },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPromoCodes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPromoCodes.fulfilled, (state, action) => {
        state.loading = false;
        state.codes = dedupeEntities(action.payload.promoCodes);
        state.pagination = action.payload.pagination;
      })
      .addCase(fetchPromoCodes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });

    builder
      .addCase(createPromoCode.pending, (state) => { state.actionLoading = true; })
      .addCase(createPromoCode.fulfilled, (state, action) => {
        state.actionLoading = false;
        if (action.payload?._id) {
          const existingIndex = state.codes.findIndex(
            (code) => code._id === action.payload._id,
          );
          if (existingIndex === -1) {
            state.codes.unshift(action.payload);
            state.pagination.total = Number(state.pagination.total || 0) + 1;
          } else {
            state.codes[existingIndex] = {
              ...state.codes[existingIndex],
              ...action.payload,
            };
          }
        }
      })
      .addCase(createPromoCode.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });

    builder
      .addCase(updatePromoCode.pending, (state) => { state.actionLoading = true; })
      .addCase(updatePromoCode.fulfilled, (state, action) => {
        state.actionLoading = false;
        const updated = action.payload;
        if (updated?._id) {
          const idx = state.codes.findIndex((c) => c._id === updated._id);
          if (idx !== -1) state.codes[idx] = { ...state.codes[idx], ...updated };
        }
      })
      .addCase(updatePromoCode.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      });
  },
});

export const { clearPromoError } = promoCodeSlice.actions;

export const selectPromoCodes = (state) => state.promoCode.codes;
export const selectPromoPagination = (state) => state.promoCode.pagination;
export const selectPromoLoading = (state) => state.promoCode.loading;
export const selectPromoActionLoading = (state) => state.promoCode.actionLoading;
export const selectPromoError = (state) => state.promoCode.error;

export default promoCodeSlice.reducer;
